import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'

import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import LoyaltyBurnInfoModal from 'loyalty/components/LoyaltyBurnInfoModal'
import LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import useLoyaltyBurnExperiment from 'loyalty/hooks/useLoyaltyBurnExperiment'
import { CLEAR_SEARCH_BURN_BATCH_CALCULATIONS, SET_SEARCH_BURN_ENABLED } from 'actions/actionConstants'

const StickyWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeaderBanner};

  &[data-below-backdrop] {
    z-index: ${zIndex.backdrop};
  }

  ${mediaQueryDown.mobile} {
    top: ${rem(48)};
    box-shadow: ${props => props.theme.shadow.bottom.small};

    &[data-flush-on-mobile] {
      top: 0;
    }
  }
`

interface Props {
  isLoggedIn: boolean
  isLoyaltyEnabled: boolean
  hasLoyaltyAccount: boolean
  InnerWrapper?: React.ComponentType<{ children?: React.ReactNode }>
  /** When true, sticks to top:0 on mobile instead of clearing the default 48px sticky header. */
  flushOnMobile?: boolean
}

function OfferPageLoyaltyPointsSlider(props: Props) {
  const {
    isLoggedIn,
    isLoyaltyEnabled,
    hasLoyaltyAccount,
    InnerWrapper = React.Fragment,
    flushOnMobile,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { charterBundleEnabled, activeFilter } = useContext(OfferPageStateContext)
  // Track activeFilter with a fade-out delay so the slider stays under the
  // Backdrop until its background-color transition (200ms) completes.
  const [belowBackdrop, setBelowBackdrop] = useState(!!activeFilter)
  useEffect(() => {
    if (activeFilter) {
      setBelowBackdrop(true)
      return
    }
    const timeout = setTimeout(() => setBelowBackdrop(false), 200)
    return () => clearTimeout(timeout)
  }, [activeFilter])
  const [tier] = useCurrentLoyaltyTier()
  const { isAnyVariant: isBurnEnabled } = useLoyaltyBurnExperiment()

  const searchBurnEnabled = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)

  const minBurnPoints = MINIMUM_POINTS_TO_APPLY

  useEffect(() => {
    dispatch({ type: CLEAR_SEARCH_BURN_BATCH_CALCULATIONS })
  }, [dispatch])

  const batchCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations ?? {})
  const maxBurnPoints = useMemo(() => {
    const maxPriceInPoints = Object.values(batchCalculations)
      .reduce((max, result) => Math.max(max, result?.data?.priceInPoints ?? 0), 0)
    return maxPriceInPoints > 0 ? Math.min(userPoints, maxPriceInPoints) : userPoints
  }, [batchCalculations, userPoints])

  const isLoadingMax = Object.values(batchCalculations).some(c => c?.fetching)

  const [localValue, setLocalValue] = useState<number>(() => userPoints)

  useEffect(() => {
    if (!searchBurnEnabled) return
    setLocalValue(maxBurnPoints)
    offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply: maxBurnPoints })
  }, [searchBurnEnabled, maxBurnPoints, offerPageDispatch])

  const handleSliderDispatch = useCallback((value: number) => {
    offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply: value })
  }, [offerPageDispatch])

  const debouncedSliderDispatch = useDebouncedCallback(handleSliderDispatch, 100, [handleSliderDispatch])

  const onSliderChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, value: number) => {
    setLocalValue(value)
    debouncedSliderDispatch(value)
  }, [debouncedSliderDispatch])

  const handleToggle = useCallback(async() => {
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    if (searchBurnEnabled) {
      setLocalValue(maxBurnPoints)
      dispatch({ type: SET_SEARCH_BURN_ENABLED, enabled: false })
      offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply: undefined })
    } else {
      setLocalValue(maxBurnPoints)
      dispatch({ type: SET_SEARCH_BURN_ENABLED, enabled: true })
      offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply: maxBurnPoints })
    }
  }, [isLoggedIn, searchBurnEnabled, maxBurnPoints, dispatch, offerPageDispatch, showModal])

  const handleReset = useCallback(() => {
    setLocalValue(minBurnPoints)
    offerPageDispatch({ type: OfferPageActions.setLoyaltyPointsToApply, pointsToApply: minBurnPoints })
  }, [offerPageDispatch, minBurnPoints])

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyBurnInfoModal />)
  }, [showModal])

  const shouldShow = useMemo(() => {
    if (charterBundleEnabled) return false
    if (!isBurnEnabled) return false
    if (!isLoyaltyEnabled) return false
    if (!isLoggedIn) return true
    if (!hasLoyaltyAccount) return false
    return userPoints >= minBurnPoints
  }, [charterBundleEnabled, isBurnEnabled, isLoyaltyEnabled, isLoggedIn, hasLoyaltyAccount, userPoints, minBurnPoints])

  if (!shouldShow) {
    return null
  }

  const sliderContent = <LoyaltyBurnSliderBase
    isEnabled={searchBurnEnabled}
    onToggle={handleToggle}
    tier={tier}
    hasLoyaltyAccount={hasLoyaltyAccount}
    userPoints={userPoints}
    maxBurnPoints={maxBurnPoints}
    minBurnPoints={minBurnPoints}
    loading={isLoadingMax}
    localValue={localValue}
    onSliderChange={onSliderChange}
    onReset={handleReset}
    onInfoClick={handleInfoClick}
  />

  return (
    <StickyWrapper
      data-below-backdrop={belowBackdrop ? '' : undefined}
      data-large-gap=""
      data-flush-on-mobile={flushOnMobile ? '' : undefined}
    >
      <InnerWrapper>{sliderContent}</InnerWrapper>
    </StickyWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  isLoggedIn: selectLoggedIn(appState),
  isLoyaltyEnabled: getIsLoyaltyEnabled(appState),
  hasLoyaltyAccount: getHasLoyaltyAccount(appState),
}), OfferPageLoyaltyPointsSlider)
